import { supabase } from '@/supabase'

// Complete interface matching the fuel_records table schema exactly
export interface AttendantFuelRecord {
  // Primary key (auto-generated)
  id?: string; // UUID auto-generated
  
  // Vehicle information
  vehicle_id?: string; // UUID, can be null
  vehicle_reg?: string; // Text, can be null
  vehicle_name?: string; // Text, can be null
  
  // Required fields
  date: string; // Date, not null
  fuel_type: string; // Text, not null
  quantity: number; // Numeric(8,2), not null
  before_odometer_reading: number; // Numeric(12,2), not null
  
  // Optional fields
  after_odometer_reading?: number; // Numeric, can be null
  location?: string; // Text, can be null
  filled_by?: string; // Text, can be null
  notes?: string; // Text, can be null
  cost_per_unit?: number; // Numeric(8,2), can be null
  total_cost?: number; // Numeric(10,2), can be null
  card_refuel?: boolean; // Boolean, can be null
  
  // Related entities
  driver_id?: string; // UUID, can be null
  driver_name?: string; // Text, can be null
  tank_id?: string; // UUID, can be null
  tank_name?: string; // Text, can be null
  depot_name?: string; // Text, can be null
  
  // Image URLs
  odo_before_image_url?: string; // Text, can be null
  odo_after_image_url?: string; // Text, can be null

  // Pump meter readings (optional, for new schema)
  pump_read_before?: number;
  pump_read_after?: number;
  pump_id?: string; // UUID for pump selection
  sub_contractor_id?: string; // UUID for subcontractor (NULL for CargoCarriers)

  // Timestamps (auto-generated)
  created_at?: string; // Timestamp with time zone, default now()
  updated_at?: string; // Timestamp with time zone, default now()
}

export const attendantRefuelService = {
  async create(fuelRecord: AttendantFuelRecord) {
    // Enhanced logging - log the exact payload being sent
    console.log('DETAILED DEBUG - Submitting to fuel_records:', JSON.stringify(fuelRecord, null, 2));
    
    try {
      // Log the table being used
      console.log('Inserting into table: fuel_records');
      
      // Create a record that exactly matches the database schema
      // Only include fields that are explicitly defined in the schema
      const recordToInsert: any = {
        // Required fields
        date: fuelRecord.date,
        fuel_type: fuelRecord.fuel_type,
        quantity: fuelRecord.quantity,

        // Legacy/required schema fields (NOT NULL constraint)
        before_odometer_reading: fuelRecord.before_odometer_reading,
        after_odometer_reading: fuelRecord.after_odometer_reading,

        // Pump Meter (PumpOdometer) fields for new schema
        ...(fuelRecord.pump_read_before !== undefined && fuelRecord.pump_read_before !== null ? { pump_read_before: parseFloat(fuelRecord.pump_read_before as any) } : {}),
        ...(fuelRecord.pump_read_after !== undefined && fuelRecord.pump_read_after !== null ? { pump_read_after: parseFloat(fuelRecord.pump_read_after as any) } : {}),

        // Vehicle information
        vehicle_id: fuelRecord.vehicle_id || null,
        vehicle_reg: fuelRecord.vehicle_reg || null,
        vehicle_name: fuelRecord.vehicle_name || null,

        // Optional fields
        location: fuelRecord.location || null,
        filled_by: fuelRecord.filled_by || null,
        notes: fuelRecord.notes || null,
        cost_per_unit: fuelRecord.cost_per_unit || null,
        total_cost: fuelRecord.total_cost || null,
        card_refuel: false, // For attendant refuel, this is always false
        driver_id: fuelRecord.driver_id || null,
        driver_name: fuelRecord.driver_name || null,
        tank_id: fuelRecord.tank_id || null,
        tank_name: fuelRecord.tank_name || null,
        depot_name: fuelRecord.depot_name || null,
        pump_id: fuelRecord.pump_id || null,
        sub_contractor_id: fuelRecord.sub_contractor_id || null,
        odo_before_image_url: fuelRecord.odo_before_image_url || null,
        odo_after_image_url: fuelRecord.odo_after_image_url || null
        // Note: before_odometer_reading and after_odometer_reading are deprecated and not included
      };
      
      console.log('RECORD FOR INSERTION:', JSON.stringify(recordToInsert, null, 2));
      
      const { data, error } = await supabase
        .from('fuel_records')
        .insert([recordToInsert])
        .select()
        .single();
      
      if (error) {
        console.error('DETAILED ERROR from Supabase:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error details:', error.details);
        console.error('Error hint:', error.hint);
        
        // Log the payload that caused the error
        console.error('Payload that caused error:', JSON.stringify(fuelRecord, null, 2));
        
        // Try to identify if there's a field name mismatch
        const errorMsg = error.message || '';
        if (errorMsg.includes('column') && errorMsg.includes('does not exist')) {
          const matches = errorMsg.match(/column ['"](.*?)['"] does not exist/);
          if (matches && matches[1]) {
            const badColumn = matches[1];
            console.error(`Bad column name detected: ${badColumn}`);
            console.error('Available fields in record:', Object.keys(fuelRecord).join(', '));
          }
        }
        
        throw error;
      }
      
      console.log('DETAILED SUCCESS - Record created:', JSON.stringify(data, null, 2));
      return { data, error: null };
    } catch (err) {
      console.error('DETAILED EXCEPTION:', err);
      throw err;
    }
  }
}
